/**
 * useReferralToken Custom Hook
 *
 * This hook provides token validation, data fetching with loading/error states,
 * and retry mechanism for network errors. Follows the established hook patterns
 * in the BiteWise codebase.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { referralDomain, ReferralApiError } from '../services/domains/ReferralDomain';
import type { Referral } from '../services/domains/ReferralDomain';
import { REFERRAL_STORAGE } from '../constants/storage';
import type {
  ReferralData,
  ReferralError,
  UseReferralTokenReturn,
  TokenValidationResult
} from '../types/referralLanding';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Map ReferralDomain Referral to ReferralLanding ReferralData
 */
function mapReferralToReferralData(referral: Referral): ReferralData {
  return {
    referralToken: referral.referral_token,
    createdAt: referral.created_at,
    updatedAt: referral.updated_at,
    expiresAt: referral.expires_at,
    status: referral.status === 'pending' ? 'pending' :
            referral.status === 'expired' ? 'expired' :
            referral.status === 'completed' ? 'used' : 'active',
    physician: {
      id: referral.referring_physician_id,
      firstName: referral.referring_physician_name?.split(' ')[0] || '',
      lastName: referral.referring_physician_name?.split(' ').slice(1).join(' ') || '',
      title: referral.referring_physician_role === 'nurse' ? '' : 'Dr.',
      specialty: '',
      organization: '',
      email: referral.referring_physician_email || '',
      phone: undefined,
      profilePhotoUrl: undefined,
    },
    dietician: {
      id: referral.dietitian_id || '',
      firstName: referral.dietitian_name?.split(' ')[0] || '',
      lastName: referral.dietitian_name?.split(' ').slice(1).join(' ') || '',
      title: '',
      credentials: [],
      specialties: [],
      organization: '',
      email: referral.dietitian_email || '',
      phone: undefined,
      profilePhotoUrl: undefined,
      bio: undefined,
      yearsOfExperience: undefined,
      schedulingUri: referral.dietitian_scheduling_uri,
    },
    patientInfo: referral.patient_name ? {
      firstName: referral.patient_name.split(' ')[0] || '',
      lastName: referral.patient_name.split(' ').slice(1).join(' ') || '',
      email: referral.patient_email,
      phone: referral.phone,
      dateOfBirth: referral.date_of_birth,
      healthConditions: referral.conditions,
    } : undefined,
    paymentGuidance: referral.payment_guidance ?? null,
  };
}

/**
 * Map ReferralApiError to ReferralError
 */
function mapReferralApiErrorToReferralError(error: ReferralApiError): ReferralError {
  let errorType: ReferralError['type'] = 'server_error';
  const message =
    typeof (error as unknown as { message?: unknown })?.message === 'string'
      ? (error as unknown as { message: string }).message.toLowerCase()
      : '';

  if (error.status === 404) {
    errorType = 'not_found';
  } else if (error.status === 410) {
    errorType = 'expired';
  } else if (error.status === 400) {
    errorType = 'invalid_token';
  } else if (!error.status || error.status >= 500) {
    errorType = 'server_error';
  } else if (message.includes('network') || message.includes('fetch')) {
    errorType = 'network_error';
  }

  return {
    type: errorType,
    message: error.message,
    code: error.code,
    details: error.fieldErrors ? { fieldErrors: error.fieldErrors } : undefined,
  };
}

/**
 * Hook configuration options
 */
interface UseReferralTokenOptions {
  /** Enable automatic retry on network errors */
  enableRetry?: boolean;
  /** Maximum number of retry attempts */
  maxRetries?: number;
  /** Retry delay in milliseconds */
  retryDelay?: number;
  /** Enable automatic refetch on window focus */
  refetchOnWindowFocus?: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_OPTIONS: Required<UseReferralTokenOptions> = {
  enableRetry: true,
  maxRetries: 3,
  retryDelay: 1000,
  refetchOnWindowFocus: false,
};

/**
 * Custom hook for referral token management
 *
 * @param token - The referral token to validate and fetch data for
 * @param options - Configuration options
 * @returns UseReferralTokenReturn - Hook state and methods
 */
export function useReferralToken(
  token: string | null | undefined,
  options: UseReferralTokenOptions = {}
): UseReferralTokenReturn {
  const logger = getLogger();
  // Merge options with defaults
  const config = { ...DEFAULT_OPTIONS, ...options };

  // State management
  const [data, setData] = useState<ReferralData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ReferralError | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);

  // Refs for cleanup and retry logic
  const retryCountRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Validate token format
   */
  const validateToken = useCallback((tokenToValidate: string): TokenValidationResult => {
    if (!tokenToValidate || typeof tokenToValidate !== 'string' || tokenToValidate.trim() === '') {
      return {
        isValid: false,
        error: 'Token is required and must be a string',
      };
    }

    if (tokenToValidate.length !== 43) {
      return {
        isValid: false,
        error: 'Token must be exactly 43 characters long',
      };
    }

    // Check base64url format (alphanumeric + - and _)
    const base64urlPattern = /^[A-Za-z0-9_-]+$/;
    if (!base64urlPattern.test(tokenToValidate)) {
      return {
        isValid: false,
        error: 'Token contains invalid characters',
      };
    }

    return { isValid: true };
  }, []);

  /**
   * Fetch referral data
   */
  const fetchReferralData = useCallback(async (
    tokenToFetch: string,
    isRetry: boolean = false
  ): Promise<void> => {
    try {
      // Validate token before fetching
      const validation = validateToken(tokenToFetch);
      if (!validation.isValid) {
        throw {
          type: 'invalid_token',
          message: validation.error || 'Invalid token format',
          code: 'VALIDATION_ERROR',
        } as ReferralError;
      }

      // Set loading states
      if (!isRetry) {
        setIsLoading(true);
        retryCountRef.current = 0;
      }
      setIsValidating(true);
      setError(null);

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Fetch data from API using ReferralDomain
      const referral = await referralDomain.getReferralByToken(tokenToFetch);

      // Transform the referral data to match the expected interface
      const referralData = mapReferralToReferralData(referral);

      // Store the referral token in localStorage after successful fetch
      try {
        localStorage.setItem(REFERRAL_STORAGE.TOKEN, tokenToFetch);
      } catch (storageError) {
        // Log storage error but don't fail the entire operation
        logger.warn('Failed to store referral token in localStorage:', storageError);
      }

      // Update state with successful data
      setData(referralData);
      setError(null);
      retryCountRef.current = 0;

    } catch (fetchError) {
      let referralError: ReferralError;

      if (fetchError instanceof ReferralApiError) {
        referralError = mapReferralApiErrorToReferralError(fetchError);
      } else if (fetchError && typeof fetchError === 'object' && 'type' in fetchError) {
        referralError = fetchError as ReferralError;
      } else {
        referralError = {
          type: 'server_error',
          message: fetchError instanceof Error ? fetchError.message : 'An unexpected error occurred',
          code: 'UNKNOWN_ERROR',
        };
      }

      // Handle retry logic for network errors
      if (
        config.enableRetry &&
        referralError.type === 'network_error' &&
        retryCountRef.current < config.maxRetries
      ) {
        retryCountRef.current += 1;

        // Schedule retry with exponential backoff
        const delay = config.retryDelay * Math.pow(2, retryCountRef.current - 1);
        timeoutRef.current = setTimeout(() => {
          fetchReferralData(tokenToFetch, true);
        }, delay);

        return;
      }

      // Set error state
      setError(referralError);
      setData(null);
    } finally {
      setIsLoading(false);
      setIsValidating(false);
      abortControllerRef.current = null;
    }
  }, [validateToken, config.enableRetry, config.maxRetries, config.retryDelay]);

  /**
   * Manual retry function
   */
  const retry = useCallback(() => {
    if (token && token.trim() !== '') {
      retryCountRef.current = 0;
      fetchReferralData(token);
    }
  }, [token, fetchReferralData]);

  /**
   * Handle window focus refetch
   */
  const handleWindowFocus = useCallback(() => {
    if (config.refetchOnWindowFocus && token && token.trim() !== '' && !isLoading && !isValidating) {
      setIsValidating(true);
      fetchReferralData(token, true);
    }
  }, [config.refetchOnWindowFocus, token, isLoading, isValidating]);

  /**
   * Effect for initial data fetching
   */
  useEffect(() => {
    if (token && token.trim() !== '') {
      // Validate token first
      const validation = validateToken(token);
      if (!validation.isValid) {
        // Set error state for invalid tokens without calling API
        setError({
          type: 'invalid_token',
          message: validation.error || 'Invalid token format',
          code: 'VALIDATION_ERROR',
        } as ReferralError);
        setData(null);
        setIsLoading(false);
        setIsValidating(false);
      } else {
        // Token is valid, proceed with API call
        fetchReferralData(token);
      }
    } else {
      // Clear state when token is null/undefined/empty
      setData(null);
      setError(null);
      setIsLoading(false);
      setIsValidating(false);
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [token]);

  /**
   * Effect for window focus listener
   */
  useEffect(() => {
    if (config.refetchOnWindowFocus) {
      window.addEventListener('focus', handleWindowFocus);
      return () => {
        window.removeEventListener('focus', handleWindowFocus);
      };
    }
  }, [config.refetchOnWindowFocus]);

  /**
   * Cleanup effect
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    isLoading,
    error,
    retry,
    isValidating,
  };
}

/**
 * Utility function to validate token format without using the hook
 *
 * @param token - Token to validate
 * @returns TokenValidationResult
 */
export function validateReferralToken(token: string): TokenValidationResult {
  if (!token || typeof token !== 'string' || token.trim() === '') {
    return {
      isValid: false,
      error: 'Token is required and must be a string',
    };
  }

  if (token.length !== 43) {
    return {
      isValid: false,
      error: 'Token must be exactly 43 characters long',
    };
  }

  // Check base64url format (alphanumeric + - and _)
  const base64urlPattern = /^[A-Za-z0-9_-]+$/;
  if (!base64urlPattern.test(token)) {
    return {
      isValid: false,
      error: 'Token contains invalid characters',
    };
  }

  return { isValid: true };
}

/**
 * Export default hook
 */
export default useReferralToken;